import { useEffect, useState } from "react";

/**
 * Drives an enter/exit CSS transition for a mount-on-demand overlay (sheet,
 * modal). `mounted` keeps the node in the DOM through the exit so the closing
 * transition can play; `entered` flips on the second animation frame after mount
 * so the open transition runs from the closed state instead of snapping.
 */
export const useTransitionState = (open: boolean, durationMs = 300) => {
  const [mounted, setMounted] = useState(open);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      return undefined;
    }
    setEntered(false);
    const timeout = setTimeout(() => setMounted(false), durationMs);
    return () => clearTimeout(timeout);
  }, [open, durationMs]);

  useEffect(() => {
    if (!mounted || !open) {
      return undefined;
    }
    // Double rAF: let the closed-state frame paint before entering so the
    // transition actually runs on open.
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setEntered(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [mounted, open]);

  return { mounted, entered };
};
